<template>
  <div class="receipt-details">
    <p v-if="entry.loading">Carregando dispositivos...</p>
    <p v-else-if="entry.error" class="error">{{ entry.error }} <button @click="loadDevices(true)">Tentar novamente</button></p>
    <p v-else-if="!entry.devices.length">Nenhum recebimento vinculado a este agendamento.</p>
    <template v-else>
      <div v-for="device in entry.devices" :key="device.device_id" class="device">
        <button class="device-button" type="button" @click="toggleDevice(device.device_id)">
          {{ expandedDevice === device.device_id ? '▾' : '▸' }}
          {{ device.device_id }} · {{ device.confirmed_media_count }} mídia(s)
        </button>
        <div v-if="expandedDevice === device.device_id" class="media-list">
          <p v-if="mediaEntry(device.device_id).loading">Carregando mídias...</p>
          <p v-else-if="mediaEntry(device.device_id).error" class="error">
            {{ mediaEntry(device.device_id).error }}
            <button @click="loadMedia(device.device_id, true)">Tentar novamente</button>
          </p>
          <p v-else-if="!mediaEntry(device.device_id).items.length">Nenhuma mídia encontrada.</p>
          <table v-else>
            <thead><tr><th>Mídia</th><th>Return Channel</th><th>Horário de referência</th><th>Tempo até confirmação</th></tr></thead>
            <tbody>
              <tr v-for="media in mediaEntry(device.device_id).items" :key="media.media_id">
                <td>{{ media.media_id }}</td>
                <td>{{ dateLabel(media.return_channel_at) }}</td>
                <td>{{ dateLabel(media.reference_at) }}</td>
                <td>{{ media.elapsed_minutes == null ? 'Indisponível' : `${media.elapsed_minutes} min` }}</td>
              </tr>
            </tbody>
          </table>
          <button v-if="mediaEntry(device.device_id).items.length < mediaEntry(device.device_id).total" type="button" :disabled="mediaEntry(device.device_id).loading" @click="loadMedia(device.device_id, false, true)">Carregar mais mídias</button>
        </div>
      </div>
      <button v-if="entry.devices.length < entry.total" type="button" :disabled="entry.loading" @click="loadDevices(false, true)">Carregar mais dispositivos</button>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { config } from "../../config";

const PAGE_SIZE = 20;
const TTL_MS = 5 * 60 * 1000;

function emptyEntry() {
  return { devices: [], total: 0, loading: false, error: null, media: {}, loadedAt: 0, pending: null };
}

export default {
  name: "ScheduleReceiptDetails",
  props: {
    schedule: { type: Object, required: true },
    cache: { type: Object, required: true },
  },
  data() {
    return { expandedDevice: null, entry: this.cache[this.schedule.id] || emptyEntry() };
  },
  mounted() {
    this.$emit("cache-update", this.schedule.id, this.entry);
    this.loadDevices();
  },
  methods: {
    mediaEntry(deviceId) {
      if (!this.entry.media[deviceId]) {
        this.entry.media[deviceId] = { items: [], total: 0, loading: false, error: null, loadedAt: 0, pending: null };
      }
      return this.entry.media[deviceId];
    },
    dateLabel(value) {
      if (!value) return "Indisponível";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Indisponível" : new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short", timeStyle: "medium", timeZone: "America/Sao_Paulo",
      }).format(date);
    },
    async loadDevices(force = false, more = false) {
      const state = this.entry;
      if (state.pending) return state.pending;
      if (!force && state.loadedAt && Date.now() - state.loadedAt < TTL_MS && (!more || state.devices.length >= state.total)) return;
      const page = more && state.loadedAt && Date.now() - state.loadedAt < TTL_MS ? Math.floor(state.devices.length / PAGE_SIZE) + 1 : 1;
      state.loading = true;
      state.error = null;
      state.pending = axios.get(`${config.API}/v1/schedule/${this.schedule.id}/devices`, {
        params: { page, page_size: PAGE_SIZE },
      }).then(({ data }) => {
        state.devices = page === 1 ? data.items : [...state.devices, ...data.items];
        state.total = data.total;
        state.loadedAt = Date.now();
      }).catch((error) => {
        state.error = error?.response?.data?.detail || "Não foi possível carregar os dispositivos.";
      }).finally(() => { state.loading = false; state.pending = null; });
      return state.pending;
    },
    toggleDevice(deviceId) {
      this.expandedDevice = this.expandedDevice === deviceId ? null : deviceId;
      if (this.expandedDevice) this.loadMedia(deviceId);
    },
    async loadMedia(deviceId, force = false, more = false) {
      const state = this.mediaEntry(deviceId);
      if (state.pending) return state.pending;
      if (!force && state.loadedAt && Date.now() - state.loadedAt < TTL_MS && (!more || state.items.length >= state.total)) return;
      const page = more && state.loadedAt && Date.now() - state.loadedAt < TTL_MS ? Math.floor(state.items.length / PAGE_SIZE) + 1 : 1;
      state.loading = true;
      state.error = null;
      state.pending = axios.get(`${config.API}/v1/schedule/${this.schedule.id}/device-media`, {
        params: { deviceID: deviceId, page, page_size: PAGE_SIZE },
      }).then(({ data }) => {
        state.items = page === 1 ? data.items : [...state.items, ...data.items];
        state.total = data.total;
        state.loadedAt = Date.now();
      }).catch((error) => {
        state.error = error?.response?.data?.detail || "Não foi possível carregar as mídias.";
      }).finally(() => { state.loading = false; state.pending = null; });
      return state.pending;
    },
  },
};
</script>

<style scoped>
.receipt-details { padding: 16px 24px; color: #2b4054; }
.device { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
.device-button { border: 0; background: transparent; color: inherit; cursor: pointer; text-align: left; }
.media-list { padding: 12px 24px; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: left; }
.error { color: #b42318; }
</style>
